import type { TFunction } from 'i18next'
import type { ListingCarouselPhoto } from '../components/ListingDetailPhotoCarousel'
import { inBoxLabelKey } from './inBoxCompleteness'
import type { ListingBuilderDraft } from './listingBuilderDraft'

export type ListingPreviewViewModel = {
  manufacturer?: string | null
  modelLabel: string
  priceLabel: string
  storageLabel: string
  yearLabel: string
  batteryLabel: string
  conditionLabel: string
  inBoxLabel: string
  warrantyLabel: string
  carouselPhotos: ListingCarouselPhoto[]
  activePhotoIndex: number
  sellerName: string
  sellerNameSub: string
  cityLabel: string
  cityValue: string
  showCompletionHint: boolean
}

function carouselPhotoId(rawId: string, index: number): number {
  const n = Number(rawId)
  if (Number.isFinite(n) && n >= 0) return n
  let h = 0
  for (let i = 0; i < rawId.length; i += 1) {
    h = (Math.imul(31, h) + rawId.charCodeAt(i)) | 0
  }
  return h !== 0 ? Math.abs(h) : index + 1
}

function parsePrice(raw: string | undefined): number {
  const n = parseFloat((raw ?? '').replace(',', '.'))
  return Number.isFinite(n) ? n : NaN
}

export function mapDraftToListingPreview(
  draft: ListingBuilderDraft,
  t: TFunction,
  options?: { activePhotoIndex?: number },
): ListingPreviewViewModel {
  const unknown = t('listing.builder.previewUnknown')
  const photos = draft.photos ?? []
  const activePhotoIndex =
    photos.length === 0
      ? 0
      : Math.min(Math.max(0, options?.activePhotoIndex ?? 0), photos.length - 1)
  const carouselPhotos: ListingCarouselPhoto[] = photos.map((p, i) => ({
    id: carouselPhotoId(p.id, i),
    thumbnail_url: p.url,
    display_url: p.url,
  }))

  const modelLabel = draft.modelName?.trim()
    ? draft.modelName.trim()
    : t('listing.builder.previewSelectPhone')

  const priceNum = parsePrice(draft.price)
  const priceLabel =
    Number.isFinite(priceNum) && priceNum > 0
      ? `€${priceNum.toFixed(2)}`
      : t('listing.builder.previewPriceNotSet')

  const conditionLabel = draft.condition
    ? t(`listing.condition.${draft.condition}`, draft.condition)
    : unknown

  const inBoxLabel = draft.inBoxCompleteness
    ? t(inBoxLabelKey(draft.inBoxCompleteness))
    : unknown

  let warrantyLabel = unknown
  if (draft.hasFactoryWarranty === true) {
    warrantyLabel = draft.warrantyRemaining
      ? t(`listing.warrantyRemaining.${draft.warrantyRemaining}`, draft.warrantyRemaining)
      : t('listing.warrantyBadgeYes')
  } else if (draft.hasFactoryWarranty === false) {
    warrantyLabel = t('listing.warrantyBadgeNo')
  }

  const yearLabel =
    draft.releaseYearUnknown || !draft.releaseYear?.trim()
      ? unknown
      : draft.releaseYear

  const batteryLabel =
    draft.batteryHealth && Number(draft.batteryHealth) > 0
      ? `${draft.batteryHealth}%`
      : unknown

  const storageLabel = draft.storage?.trim() || unknown

  const sellerTrim = draft.sellerName?.trim() ?? ''
  const sellerName = sellerTrim || t('listing.builder.previewSellerPlaceholder')
  const sellerNameSub = sellerTrim ? '' : t('listing.builder.previewNamePlaceholder')

  const cityTrim = draft.sellerCity?.trim() ?? ''
  const cityValue = cityTrim || t('listing.builder.previewCityPlaceholder')

  return {
    manufacturer: draft.manufacturer,
    modelLabel,
    priceLabel,
    storageLabel,
    yearLabel,
    batteryLabel,
    conditionLabel,
    inBoxLabel,
    warrantyLabel,
    carouselPhotos,
    activePhotoIndex,
    sellerName,
    sellerNameSub,
    cityLabel: t('listing.builder.previewCityLabel'),
    cityValue,
    showCompletionHint: !draft.allStepsComplete,
  }
}
